import { zonedDate } from '../../../../utils/date-time';
import { useEffect, useMemo, useState } from 'react';
import { urlFile } from '../../../../AxiosConfig';
import { formatToReal } from '../../../../components/utils';
import { ServicosService } from '../../../servicos/Service';
import { formatDuration, LoadFailure } from '../../booking/BookingParts';
import { useFormHorarioClienteContext } from './Context';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { AgendaClienteService } from '../Service';
import moment from 'moment';
import { Check, RotateCw, X } from 'lucide-react';

const service = new ServicosService();
const agendaClienteService = new AgendaClienteService();

export const SelectServico = () => {
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [servicos, setServicos] = useState([] as any);
    const [lastSchedule, setLastSchedule] = useState<any | null>(null);
    const [closeSuggestion, setCloseSuggestion] = useState(false);
    const { user } = useAuthContext();
    const {
        estab,
        atendente,
        servico,
        setServico,
        servicosExtras,
        setServicosExtras,
        setHorario,
        setShowHorarios,
    } = useFormHorarioClienteContext();

    const loadServicos = async () => {
        if (!estab?._id || !atendente?.uid) {
            setServicos([]);
            setServico({} as any);
            setServicosExtras([]);
            return;
        }

        try {
            setLoading(true);
            setLoadError(false);
            const { data } = await service.getByEstabId(estab._id, atendente.uid);
            setServicos(Array.isArray(data) ? data : []);
        } catch (err) {
            setServicos([]);
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    };

    const loadLastSchedule = async () => {
        if (!estab?._id || !user?.uid) {
            setLastSchedule(null);
            return;
        }

        try {
            const { data } = await agendaClienteService.getUltimoAgendamentoByEstab(estab._id);
            setLastSchedule(data || null);
        } catch (error) {
            setLastSchedule(null);
        }
    };

    useEffect(() => {
        if (!!estab?._id && !!atendente?.uid) loadServicos();
        else setServicos([]);
    }, [estab?._id, atendente?.uid]);

    useEffect(() => {
        loadLastSchedule();
    }, [estab?._id, user?.uid]);

    useEffect(() => {
        setCloseSuggestion(false);
    }, [estab?._id, atendente?.uid]);

    useEffect(() => {
        // Enquanto a lista carrega (ex.: ao voltar de horarios), ainda nao da para saber se o servico saiu.
        if (loading || loadError || !servico?._id) return;
        if (servicos.some((item: any) => item?._id === servico._id)) return;

        setServico({} as any);
        setServicosExtras([]);
        setHorario(0);
        setShowHorarios(false);
    }, [servicos, servico?._id, loading, loadError]);

    const lastScheduleServices = useMemo(() => {
        if (!lastSchedule || !servicos.length) return null;

        const serviceMap = new Map(
            servicos.map((item: any) => [String(item?._id || ''), item]),
        );

        const suggestedMainId = lastSchedule?.servicoId || lastSchedule?.service?._id;
        const suggestedExtrasIds = Array.isArray(lastSchedule?.servicosExtrasIds)
            ? lastSchedule.servicosExtrasIds
            : (lastSchedule?.servicosExtras || []).map((extra: any) => extra?._id);

        const suggestedMain = serviceMap.get(String(suggestedMainId || ''));
        const suggestedExtras = (suggestedExtrasIds || [])
            .map((id: string) => serviceMap.get(String(id || '')))
            .filter(Boolean);

        if (!suggestedMain && !suggestedExtras.length) return null;

        const main = suggestedMain || suggestedExtras[0];
        const extras = suggestedExtras.filter(
            (extra: any) => String(extra?._id || '') !== String(main?._id || ''),
        );

        const services = [main, ...extras].filter(Boolean);

        return {
            main,
            extras,
            services,
            startAt: lastSchedule?.startAt,
        };
    }, [lastSchedule, servicos]);

    const applyLastServices = () => {
        if (!lastScheduleServices?.main) return;
        setHorario(0);
        setShowHorarios(false);
        setServico(lastScheduleServices.main);
        setServicosExtras(lastScheduleServices.extras);
    };

    const selectedServicesKey = useMemo(() => {
        return [servico?._id, ...servicosExtras.map((item: any) => item?._id)]
            .filter(Boolean)
            .map((id: any) => String(id))
            .sort()
            .join('|');
    }, [servico?._id, servicosExtras]);

    const suggestedServicesKey = useMemo(() => {
        return (lastScheduleServices?.services || [])
            .map((item: any) => String(item?._id || ''))
            .filter(Boolean)
            .sort()
            .join('|');
    }, [lastScheduleServices]);

    const isLastServicesApplied = Boolean(suggestedServicesKey) && selectedServicesKey === suggestedServicesKey;

    const handleToggleServico = (item: any) => {
        setHorario(0);
        setShowHorarios(false);

        if (!servico?._id) {
            setServico(item);
            return;
        }

        if (servico._id === item._id) {
            if (servicosExtras.length) {
                const [nextMain, ...rest] = servicosExtras;
                setServico(nextMain);
                setServicosExtras(rest);
            } else {
                setServico({} as any);
            }
            return;
        }

        const isExtra = servicosExtras.some((extra) => extra._id === item._id);
        if (isExtra) {
            setServicosExtras((prev: any[]) =>
                prev.filter((extra) => extra._id !== item._id),
            );
            return;
        }

        setServicosExtras((prev: any[]) => [...prev, item]);
    };


    const isPrimary = (itemId: string) => servico?._id === itemId;
    const isExtra = (itemId: string) => servicosExtras.some((extra) => extra._id === itemId);

    if (loading) {
        return (
            <div className="space-y-2" role="status" aria-label="Carregando serviços">
                {[0, 1, 2, 3].map((item) => <div key={item} className="sf-skeleton h-20 rounded-2xl" />)}
            </div>
        );
    }

    if (loadError) {
        return <LoadFailure message="Não foi possível carregar os serviços." onRetry={loadServicos} />;
    }

    if (!servicos.length) {
        return <p className="py-10 text-center text-sm text-base-content/70">Nenhum serviço disponível com este profissional.</p>;
    }

    const suggestionLabel = (lastScheduleServices?.services || []).map((item: any) => item?.name).filter(Boolean).join(' + ');

    return (
        <div className="space-y-4">
            {!closeSuggestion && !!suggestionLabel && !isLastServicesApplied && (
                <div className="flex items-center gap-3 rounded-2xl bg-primary/10 py-3 pl-4 pr-2">
                    <RotateCw className="text-sm text-primary" aria-hidden="true" />
                    <button type="button" className="min-w-0 flex-1 text-left" onClick={applyLastServices}>
                        <span className="block text-xs text-base-content/60">
                            Repetir o último{lastScheduleServices?.startAt ? ` · ${zonedDate(lastScheduleServices.startAt, estab?.timezone).format('DD/MM')}` : ''}
                        </span>
                        <span className="block truncate text-sm font-semibold text-base-content">{suggestionLabel}</span>
                    </button>
                    <button type="button" className="px-2 text-sm font-semibold text-primary" onClick={applyLastServices}>Usar</button>
                    <button
                        type="button"
                        aria-label="Dispensar sugestão"
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs text-base-content/50"
                        onClick={() => setCloseSuggestion(true)}
                    >
                        <X aria-hidden="true" />
                    </button>
                </div>
            )}

            <div>
                <p className="mb-2 px-1 text-sm text-base-content/60">Você pode escolher mais de um.</p>
                <ul className="overflow-hidden rounded-2xl bg-base-100 ring-1 ring-base-content/[0.06]">
                    {servicos.map((item: any) => {
                        const selected = isPrimary(item._id) || isExtra(item._id);
                        const image = item?.imagesIds?.[0];
                        return (
                            <li key={item._id} className="border-b border-base-content/[0.08] last:border-b-0">
                                <button
                                    type="button"
                                    aria-pressed={selected}
                                    className={`flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors ${selected ? 'bg-primary/[0.07]' : 'active:bg-base-content/5'}`}
                                    onClick={() => handleToggleServico(item)}
                                >
                                    {!!image && <img src={`${urlFile}/${encodeURIComponent(image)}`} alt="" loading="lazy" className="h-14 w-14 shrink-0 rounded-xl bg-base-200 object-cover" />}
                                    <span className="min-w-0 flex-1">
                                        <span className="block break-words text-[0.9375rem] font-semibold leading-snug text-base-content">{item?.name}</span>
                                        {!!item?.description && <span className="mt-0.5 line-clamp-2 text-sm leading-snug text-base-content/55">{item.description}</span>}
                                        <span className="mt-1 block text-sm tabular-nums text-base-content/70">
                                            {!!item?.duration && `${formatDuration(item.duration)} · `}
                                            <span className="font-medium text-base-content">{Number(item?.price) > 0 ? `R$ ${formatToReal(item.price)}` : 'Valor a combinar'}</span>
                                        </span>
                                    </span>
                                    <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[0.6875rem] transition-colors ${selected ? 'bg-primary text-primary-content' : 'ring-1 ring-inset ring-base-content/20'}`} aria-hidden="true">
                                        {selected && <Check aria-hidden="true" />}
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
};
